import { __awaiter, __generator } from "tslib";
import { isDefined, Platform } from '@tyler-components-web/core';
import { DRAWER_CONSTANTS } from './drawer-constants';
var DrawerFoundation = /** @class */ (function () {
    function DrawerFoundation(_adapter) {
        var _this = this;
        this._adapter = _adapter;
        this._open = true;
        this._type = 'permanent';
        this._direction = 'left';
        this._autoClose = true;
        this._hasInitialized = false;
        this._backdropClickListener = function (evt) { return _this._onBackdropClick(evt); };
        this._openAnimationListener = function () { return _this._onOpenComplete(); };
        this._closeAnimationListener = function () { return _this._onCloseComplete(); };
    }
    DrawerFoundation.prototype.connect = function () {
        this._hasInitialized = true;
        this._syncDirectionState();
        if (this._open && this._type === 'modal') {
            this._setBackdrop(true);
        }
    };
    DrawerFoundation.prototype.disconnect = function () {
        this._hasInitialized = false;
    };
    DrawerFoundation.prototype._onBackdropClick = function (evt) {
        this.open = false;
        this._adapter.emitHostEvent(DRAWER_CONSTANTS.events.CLOSE);
    };
    DrawerFoundation.prototype._onOpenComplete = function () {
        if (!this._open) {
            return;
        }
        this._adapter.emitHostEvent(DRAWER_CONSTANTS.events.AFTER_OPEN);
    };
    DrawerFoundation.prototype._onCloseComplete = function () {
        if (this._open) {
            return;
        }
        this._adapter.removeDrawerClass(DRAWER_CONSTANTS.classes.NO_TRANSITION);
        this._adapter.emitHostEvent(DRAWER_CONSTANTS.events.AFTER_CLOSE);
    };
    DrawerFoundation.prototype._syncDirectionState = function () {
        this._adapter.setDirection(this._direction);
        this._adapter.setHostAttribute(DRAWER_CONSTANTS.attributes.DIRECTION, isDefined(this._direction) ? this._direction.toString() : '');
    };
    DrawerFoundation.prototype._syncTypeState = function () {
        this._adapter.removeDrawerClass([
            DRAWER_CONSTANTS.classes.MODAL,
            DRAWER_CONSTANTS.classes.MINI,
            DRAWER_CONSTANTS.classes.MINI_HOVER
        ]);
        switch (this._type) {
            case 'modal':
                this._adapter.setDrawerClass(DRAWER_CONSTANTS.classes.MODAL);
                break;
            case 'mini':
            case 'mini-hover':
                this._adapter.setDrawerClass(DRAWER_CONSTANTS.classes.MINI);
                if (this._type === 'mini-hover') {
                    this._adapter.setDrawerClass(DRAWER_CONSTANTS.classes.MINI_HOVER);
                }
                break;
        }
        var attrVal = isDefined(this._type) ? this._type.toString() : '';
        this._adapter.setHostAttribute(DRAWER_CONSTANTS.attributes.TYPE, attrVal);
    };
    DrawerFoundation.prototype._syncDrawerOpenState = function () {
        if (this._open) {
            this._setDrawerOpenState();
        }
        else {
            this._setDrawerClosedState();
        }
        var attrVal = isDefined(this._open) ? this._open.toString() : '';
        this._adapter.setHostAttribute(DRAWER_CONSTANTS.attributes.OPEN, attrVal);
        this._handleModalDrawerStateInIE();
    };
    DrawerFoundation.prototype._handleModalDrawerStateInIE = function () {
        // In IE, modal drawer needs it's css display property changed to none in order to function.
        if (!Platform.TRIDENT || this._type !== 'modal') {
            return;
        }
        if (this._open) {
            this._adapter.removeDrawerClass(DRAWER_CONSTANTS.classes.DISPLAY_NONE);
        }
        else {
            this._adapter.setDrawerClass(DRAWER_CONSTANTS.classes.DISPLAY_NONE);
        }
    };
    DrawerFoundation.prototype._setDrawerOpenState = function () {
        this._adapter.listenTransitionComplete(this._openAnimationListener);
        this._adapter.removeDrawerClass(DRAWER_CONSTANTS.classes.CLOSED);
        if (this._type === 'modal') {
            this._setBackdrop(true);
        }
    };
    DrawerFoundation.prototype._setDrawerClosedState = function () {
        this._adapter.listenTransitionComplete(this._closeAnimationListener);
        this._adapter.setDrawerClass(DRAWER_CONSTANTS.classes.CLOSED);
        if (this._type === 'modal') {
            this._setBackdrop(false);
        }
    };
    DrawerFoundation.prototype._setBackdrop = function (open) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!open) return [3 /*break*/, 1];
                        this._adapter.setBackdrop(true, this._backdropClickListener);
                        return [3 /*break*/, 4];
                    case 1:
                        if (!this._hasInitialized) return [3 /*break*/, 3];
                        return [4 /*yield*/, this._adapter.setBackdrop(false, this._backdropClickListener)];
                    case 2:
                        _a.sent();
                        _a.label = 3;
                    case 3:
                        if (!this._open || this._type !== 'modal') {
                            this._adapter.removeBackdrop();
                        }
                        _a.label = 4;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    DrawerFoundation.prototype._handleModalType = function () {
        var _this = this;
        if (this._open && this._type === 'modal') {
            // If we are changing to the modal type after initialization, we always start with it closed
            if (this._hasInitialized) {
                if (this._autoClose) {
                    window.requestAnimationFrame(function () {
                        _this._adapter.removeBackdrop();
                        _this._adapter.setDrawerClass(DRAWER_CONSTANTS.classes.NO_TRANSITION);
                        _this.open = false;
                        window.requestAnimationFrame(function () { return _this._onCloseComplete(); });
                    });
                }
                // If during initialization the drawer is open and modal, we apply the backdrop
                this._setBackdrop(true);
            }
        }
        else {
            // If it is not modal type or not open, remove the backdrop
            this._adapter.removeBackdrop();
        }
    };
    DrawerFoundation.prototype._handlePermanentType = function () {
        var _this = this;
        // If we are changing to the permanent type, we always set the open state to true
        if (this._type === 'permanent') {
            // Using requestAnimationFrame because we need this to execute after the current task completes
            requestAnimationFrame(function () { return _this.open = true; });
        }
    };
    Object.defineProperty(DrawerFoundation.prototype, "open", {
        /** Toggles whether a `dismissible` or `modal` drawer is open or not. Has no effect on `permanent` drawers. */
        get: function () {
            return this._open;
        },
        set: function (value) {
            if (this._open === value) {
                return;
            }
            if (this._type === 'permanent') {
                this._open = true;
            }
            else {
                this._open = value;
            }
            this._syncDrawerOpenState();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DrawerFoundation.prototype, "type", {
        /** Gets/Sets the type of drawer to use: `permanent` (default), `dismissible`, `modal`. */
        get: function () {
            return this._type;
        },
        set: function (value) {
            if (this._type === value) {
                return;
            }
            this._type = value;
            this._handleModalType();
            this._handlePermanentType();
            this._syncTypeState();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DrawerFoundation.prototype, "direction", {
        /** Controls the animation direction of the drawer for positioning on the left vs. right side of the screen. */
        get: function () {
            return this._direction;
        },
        set: function (value) {
            if (this._direction === value) {
                return;
            }
            this._direction = value;
            this._syncDirectionState();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DrawerFoundation.prototype, "autoClose", {
        get: function () {
            return this._autoClose;
        },
        set: function (value) {
            this._autoClose = value;
        },
        enumerable: true,
        configurable: true
    });
    return DrawerFoundation;
}());
export { DrawerFoundation };
